import { useState } from "react";
import { toast } from "sonner";
import { Mail, Instagram, Calendar } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Overline } from "../components/Overline";
import { SITE } from "../lib/site";
import { Seo } from "../components/Seo";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        subject: form.subject || null,
        message: form.message,
      });
      toast.success("Thank you - Lara will be in touch within 2 working days.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please email hello@aimahealth.co.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="bg-[#faf9f6]">
      <Seo title="Contact" description="Book a complimentary discovery call or send Lara a message. Replies within two working days." path="/contact" />
      <section
        data-testid="contact-booking-section"
        className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28"
      >
        <div className="max-w-3xl">
          <Overline>Get in touch</Overline>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.04] font-normal text-[#333] mt-5">
            Let's begin
            <br />
            <span className="italic">with a conversation.</span>
          </h1>
          <p className="mt-8 text-lg text-[#3d3d3d] font-normal leading-relaxed">
            Book a complimentary 20-minute discovery call, or send me a note.
            I read and reply to every message personally - usually within two
            working days.
          </p>
        </div>

        <div className="mt-16 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="bg-white border border-[#e5e0d8] rounded-3xl p-8 md:p-10">
              <h2 className="font-serif text-3xl text-[#333] font-normal tracking-tight">
                Get in touch here
              </h2>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="mt-8 space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-sm text-[#3d3d3d]">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      required
                      data-testid="contact-name"
                      value={form.name}
                      onChange={update("name")}
                      className="mt-2 bg-[#faf9f6]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm text-[#3d3d3d]">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      data-testid="contact-email"
                      value={form.email}
                      onChange={update("email")}
                      className="mt-2 bg-[#faf9f6]"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone" className="text-sm text-[#3d3d3d]">
                      Phone (optional)
                    </Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      className="mt-2 bg-[#faf9f6]"
                    />
                  </div>
                  <div>
                    <Label
                      htmlFor="subject"
                      className="text-sm text-[#3d3d3d]"
                    >
                      What's on your mind?
                    </Label>
                    <Input
                      id="subject"
                      placeholder="e.g. PCOS, perimenopause..."
                      data-testid="contact-subject"
                      value={form.subject}
                      onChange={update("subject")}
                      className="mt-2 bg-[#faf9f6]"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-sm text-[#3d3d3d]">
                    Tell me a little of your story *
                  </Label>
                  <Textarea
                    id="message"
                    required
                    rows={6}
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    className="mt-2 bg-[#faf9f6] resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full px-8 h-12"
                >
                  {loading ? "Sending..." : "Send message"}
                </Button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div
              data-testid="calendly-embed-block"
              className="bg-[#cbdad6]/40 border border-[#e5e0d8] rounded-3xl p-8"
            >
              <Calendar className="text-[#5fa292]" size={24} />
              <h3 className="font-serif text-2xl text-[#333] mt-4 font-normal tracking-tight">
                Book a discovery call
              </h3>
              <p className="mt-3 text-[#3d3d3d] font-normal text-sm leading-relaxed">
                Twenty minutes, complimentary, no pressure. We meet by video.
              </p>
              <a
                href={SITE.calendlyUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-calendly-link"
              >
                <Button className="mt-5 w-full bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full h-11">
                  Open booking calendar
                </Button>
              </a>
            </div>

            <div className="bg-white border border-[#e5e0d8] rounded-3xl p-8 space-y-5">
              <h3 className="font-serif text-2xl text-[#333] font-normal tracking-tight">
                Or reach me directly
              </h3>
              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-[#3d3d3d] hover:text-[#5fa292] transition-colors"
              >
                <Mail size={18} className="text-[#5fa292]" />
                <span className="font-normal">{SITE.email}</span>
              </a>
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram-link"
                className="flex items-center gap-3 text-[#3d3d3d] hover:text-[#5fa292] transition-colors"
              >
                <Instagram size={18} className="text-[#5fa292]" />
                <span className="font-normal">@aima.health</span>
              </a>
              <p className="text-xs text-[#3d3d3d] pt-4 border-t border-[#e5e0d8] font-normal leading-relaxed">
                Mon-Thu · Replies within 2 working days.
                <br />
                For urgent medical concerns, please contact your GP or 111.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
